/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useNotification from '../../../hooks/useNotification'
import useForm from '../../../hooks/useForm'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  isValidCommercialDate,
} from '../../../utils/objectUtils'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { billingFields } from '../../../constants/forms/faturamento/billFields'
import Form from '../../../components/forms/Form'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import useBaseService from '../../../hooks/services/useBaseService'
import { useBillPageLogic } from './hooks/useBillPageLogic'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { entities } from '../../../constants/entities'
import BillSpreadsheet from './components/BillSpreadsheet'
import { Alert, Box } from '@mui/material'
import { useBillingExtractor } from './hooks/useBillingExtractor'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSpreadsheetAutoSave } from './hooks/useSpreadsheetAutoSave'
import { BillingEnum } from '../../../enums/billing-enum'
import Button from '../../../components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'

const EditBillPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()

  const [billingSection] = billingFields
  const [billingFieldsState, setBillingFieldsState] = useState(
    billingSection.fields
  )
  const [univerAPI, setUniverAPI] = useState(null)
  const [resetKey, setResetKey] = useState(0)

  const {
    get: baseServiceGet,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  // Formulário
  const initialValues = useMemo(
    () => setDefaultFieldValues([billingSection]),
    [billingSection]
  )

  const { formData, handleChange, setFormData, resetForm } = useForm(
    initialValues,
    [billingSection]
  )

  // Carregar dados do billing
  const { data: billData, isLoading: isLoadingBill } = useQueryWithLoader({
    queryKey: ['billing', id],
    queryFn: async () => {
      const response = await baseServiceGet(entities.billing.getById(id))
      return response.result
    },
    enabled: !!id,
    select: (data) => {
      return {
        billing: {
          bill_type: data.bill_type,
          customer_id: data.customer_id,
          contract_id: data.contract_id,
          number: data.number,
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
          organization_id: data.organization_id,
        },
        items: data.items.map((item) => ({
          id: item.id,
          alias: item.alias,
          description: item.description,
          quantity: item.quantity,
          price: item.price,
        })),
        spreadsheet_snapshot: data.spreadsheet_snapshot,
        metadata: data.metadata,
      }
    },
  })

  // Preencher formulário com dados carregados
  useEffect(() => {
    if (billData) {
      setFormData({
        billing: billData.billing,
      })
    }
  }, [billData, setFormData])

  // Lógica dos selects (reutilizando useBillPageLogic)
  const { contract, separate, selects } = useBillPageLogic({
    mode: 'edit',
    billingFields: billingFieldsState,
    setBillingFields: setBillingFieldsState,
    formData,
    setFormData,
    resetForm,
    baseServiceGet,
    initialBillingData: billData?.billing,
  })

  const billType = selects.billTypeSelect.selectedValue?.value
  const organizationId = selects.organizationSelect.selectedValue?.value
  const byContract = billType === 'by_contract'
  const isSeparate = billType === 'separate'

  const shouldFetchBillingData =
    !!organizationId &&
    ((byContract && !!contract?.id) ||
      (isSeparate && !!selects.customerSelect.selectedValue?.value))

  // Extração de dados da planilha
  const { extractBillingData } = useBillingExtractor(billType)

  // Rascunho (usando o id do billing como chave)
  const draftKey = useMemo(() => {
    if (!id) return null
    return `bill_draft_${id}`
  }, [id])

  const { loadDraft, clearDraft } = useSpreadsheetAutoSave(
    univerAPI,
    draftKey,
    !!draftKey
  )

  const handleResetDraft = useCallback(() => {
    clearDraft()
    setResetKey((prev) => prev + 1)
    showNotification('info', 'Rascunho resetado com sucesso!')
  }, [clearDraft, showNotification])

  // Quando a planilha estiver pronta, carregar o snapshot salvo
  const handleSpreadsheetReady = useCallback(
    (api) => {
      console.log('✅ Planilha pronta!')
      setUniverAPI(api)

      if (billData?.spreadsheet_snapshot) {
        clearDraft()
      } else {
        const draftSnapshot = loadDraft()
        if (draftSnapshot) {
          try {
            const currentWorkbook = api.getActiveWorkbook()
            if (currentWorkbook) {
              api.disposeUnit(currentWorkbook.getId())
            }
            api.createWorkbook(draftSnapshot)
            showNotification('info', 'Rascunho carregado automaticamente')
          } catch (error) {
            console.error('❌ Erro ao carregar rascunho:', error)
          }
        }
      }
    },
    [billData, loadDraft, clearDraft, showNotification]
  )

  // Mutation para atualizar
  const updateBillingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePut(entities.billing.update(id), data)
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        resetForm()
        queryClient.invalidateQueries({ queryKey: ['billing', id] })
        queryClient.invalidateQueries({ queryKey: ['billings'] })
        clearDraft()
        navigate('/faturamento/faturar/')
      }
    },
    onError: (error) => {
      console.error('Erro ao atualizar:', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
    },
  })

  // Verificar se pode editar (apenas draft ou pending)
  const canEdit = useMemo(() => {
    const status = formData?.billing?.status
    return status === 'draft' || status === 'pending'
  }, [formData])

  const handleSubmit = async () => {
    if (isEmptySeparateBilling) return
    if (!canEdit) {
      showNotification('warning', 'Este faturamento não pode mais ser editado.')
      return
    }

    try {
      if (!univerAPI) {
        showNotification('error', 'Planilha não está pronta. Aguarde...')
        return
      }

      const fWorkbook = univerAPI.getActiveWorkbook()
      if (!fWorkbook) {
        showNotification('error', 'Erro ao acessar planilha')
        return
      }

      const spreadsheetSnapshot = fWorkbook.save()
      const extractedData = extractBillingData(univerAPI, billType, formData)
      const { items, totals, metadata } = extractedData

      if (items.length === 0) {
        showNotification(
          'warning',
          'Nenhum item encontrado na planilha. Verifique os dados.'
        )
        return
      }

      const cleanedFormData = cleanPayload(formData)
      const hasDates =
        cleanedFormData.billing.start_date && cleanedFormData.billing.end_date

      if (!hasDates) {
        showNotification(
          'warning',
          'Preencha as datas de início e fim no formulário.'
        )
        return
      }

      // Cria um mapa dos itens existentes por alias
      const existingItemsMap = {}
      billData?.items?.forEach((item) => {
        existingItemsMap[item.alias] = item.id
      })

      // Montar items com id (para atualizar) ou sem id (para criar)
      const itemsPayload = items.map((item) => {
        const existingId = existingItemsMap[item.alias]
        return {
          ...(existingId ? { id: existingId } : {}),
          alias: item.alias,
          description: item.description,
          quantity: item.quantity,
          price: item.price,
        }
      })

      const status = !cleanedFormData.billing.number
        ? BillingEnum.DRAFT
        : BillingEnum.PENDING

      const payload = {
        billing: {
          ...cleanedFormData.billing,
          bill_type: billType,
          number:
            cleanedFormData.billing.number != null
              ? String(cleanedFormData.billing.number).trim()
              : null,
          contract_id: contract?.id || null,
          customer_id: selects.customerSelect.selectedValue?.value || null,
          organization_id: cleanedFormData.billing?.organization_id,
          status,
        },
        items: itemsPayload,
        spreadsheet_snapshot: spreadsheetSnapshot,
        metadata: {
          ...metadata,
          form_data: cleanedFormData,
          totals: totals,
        },
      }

      await updateBillingMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar faturamento:', error)
    }
  }

  const { data: billingResponse, isLoading: isLoadingBilling, isSuccess: isBillingDataSuccess } =
    useQueryWithLoader({
      queryKey: [
        'billingData',
        billType,
        organizationId,
        contract?.id,
        selects.customerSelect.selectedValue?.value,
        formData.billing.start_date,
        formData.billing.end_date,
      ],
      queryFn: async () => {
        if (
          !shouldFetchBillingData ||
          !organizationId ||
          !selects.customerSelect.selectedValue?.value ||
          !formData.billing.start_date ||
          !formData.billing.end_date
        ) {
          return null
        }

        const filters = {
          contract_id: byContract ? contract?.id : null,
          organization_id: organizationId,
          bill_type: billType,
          recipient_customer_id: selects.customerSelect.selectedValue?.value,
          start_date: formData.billing.start_date,
          end_date: formData.billing.end_date,
        }

        const response = await baseServiceGet(entities.billing.data.get, {
          filters,
        })

        return response.result
      },
      enabled:
        shouldFetchBillingData &&
        !!organizationId &&
        !!selects.customerSelect.selectedValue?.value &&
        isValidCommercialDate(formData.billing.start_date) &&
        isValidCommercialDate(formData.billing.end_date),
      staleTime: 5 * 60 * 1000,
    })

  const {
    data: contractServicesResponse,
    isLoading: isLoadingContractServices,
  } = useQueryWithLoader({
    queryKey: ['contractServicesForBilling', contract?.id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.jobs(contract.id),
        { page: 1, perPage: 1000 }
      )
      const services = response.result?.data ?? response.result ?? []
      return services.map((service) => ({
        ...service,
        quantity: Number(service.quantity) || 0,
        price: Number(service.price) || 0,
      }))
    },
    enabled: byContract && !!contract?.id,
  })

  // Extrai os dados da resposta
  const contractItems = billingResponse?.contract_items || []
  const contractServices = contractServicesResponse || []
  const typeRentedEquipments = billingResponse?.rented_types || []
  const equipments = billingResponse?.equipments || []
  const monthlyHistory = billingResponse?.monthly_history || []

  // Converte monthlyHistory para o formato esperado pelo BillSpreadsheet
  const monthlyEquipments = monthlyHistory.map((month) => ({
    reference: month.reference,
    month: month.month,
    equipments: month.equipments,
  }))

  // Organiza os dados mensais
  const monthlyData = useMemo(() => {
    const result = {}
    monthlyHistory.forEach((month) => {
      result[month.reference] = {
        contractItems: month.contract_items || [],
        rentedEquipments: month.rented_types || [],
      }
    })
    return result
  }, [monthlyHistory])

  // Condição de carregamento
  const isLoaded =
    shouldFetchBillingData && billingResponse !== undefined && !isLoadingBilling &&
    (!byContract || (!isLoadingContractServices && contractServicesResponse !== undefined))
  const isEmptySeparateBilling =
    isSeparate &&
    isBillingDataSuccess &&
    !isLoadingBilling &&
    !billData?.spreadsheet_snapshot &&
    (billData?.items?.length ?? 0) === 0 &&
    Array.isArray(billingResponse?.rented_types) &&
    billingResponse.rented_types.length === 0 &&
    Array.isArray(billingResponse?.equipments) &&
    billingResponse.equipments.length === 0

  // Se não puder editar, exibir mensagem
  if (!isLoadingBill && !canEdit) {
    return (
      <MainLayout selectedCompany="ALUCOM">
        <PageHeader
          title="Editar faturamento"
          showBackButton
          backUrl="/faturamento/faturar/"
        />
        <div className="container-fluid p-1">
          <div className="alert alert-warning">
            Este faturamento não pode ser editado pois está com status "
            {formData?.billing?.status}". Apenas faturamentos com status "draft"
            ou "pending" podem ser editados.
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar faturamento"
        showBackButton
        backUrl="/faturamento/faturar/"
      />
      <div className="container-fluid p-1" style={{ position: 'relative' }}>
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Atualizar faturamento"
          textLoadingSubmit="Atualizando faturamento..."
          handleBack={() => navigate('/faturamento/faturar/')}
          disabledSubmitButton={!isLoaded || !canEdit || isEmptySeparateBilling}>
          {() => (
            <Box key={billingSection.section} sx={{ mb: 2 }}>
              <SectionHeader section={billingSection.section} />

              <SimpleBody
                fields={billingFieldsState}
                formErrors={normalizeFormErrors(formErrors)}
                formData={formData}
                handleFieldChange={handleChange}
                getOptions={selects.getOptions}
                getSelectedValue={selects.getSelectedValue}
                getSelectMeta={selects.getSelectMeta}
              />
            </Box>
          )}
        </Form>
        {isEmptySeparateBilling && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Não há itens avulsos entregues para este cliente nesta organização no período selecionado.
          </Alert>
        )}
        {isLoaded && !isEmptySeparateBilling && (
          <Box sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleResetDraft}
                disabled={!draftKey}
                text="Resetar Rascunho"
                endIcon={<FontAwesomeIcon icon={faRefresh} />}
              />
            </Box>
            <BillSpreadsheet
              key={[
                billType,
                contract?.id,
                separate?.recipient_customer_id,
                resetKey,
              ].join('-')}
              contractItems={
                billData?.spreadsheet_snapshot ? [] : contractItems
              }
              contractServices={
                billData?.spreadsheet_snapshot ? [] : contractServices
              }
              rentedEquipments={
                billData?.spreadsheet_snapshot ? [] : typeRentedEquipments
              }
              equipmentsDetails={
                billData?.spreadsheet_snapshot ? [] : equipments
              }
              monthlyData={billData?.spreadsheet_snapshot ? {} : monthlyData}
              monthlyEquipments={
                billData?.spreadsheet_snapshot ? [] : monthlyEquipments
              }
              startDate={formData.billing.start_date}
              billType={billType}
              initialSnapshot={billData?.spreadsheet_snapshot}
              onReady={handleSpreadsheetReady}
            />
          </Box>
        )}
      </div>
    </MainLayout>
  )
}

export default EditBillPage
